import { useMemo, type ReactNode } from 'react'
import { connect } from 'react-redux'
import type moment from 'moment'
import { getCharterFlightDepartureDate } from 'checkout/selectors/view/flights'
import { useAppSelector } from 'hooks/reduxHooks'
import { isCharterCancellationPolicy } from 'lib/offer/cancellationPolicyUtils'
import { isCharterEnabled } from 'selectors/featuresSelectors'
import { getFlashCancellationPolicyView } from './getFlashCancellationPolicyView'
import FlashCancellationPoliciesView from './FlashCancellationPoliciesView'

interface Props {
  offerId?: string
  uniqueKey?: string
  propertyTimezoneOffset?: number
  cancellationPolicy?: App.OfferCancellationPolicy
  checkInDate?: string | moment.Moment
  /**
   * Charter departure date when available. Kept separate from hotel check-in so flexible
   * cancellation can hide while departure is still resolving.
   *
   * When this prop is omitted, charter policies fall back to the checkout cart departure
   * date (Review / Change package / Bundle tiles). Call sites that manage their own
   * resolve cycle must pass the prop explicitly (even as undefined) so a stale cart date
   * is not used — e.g. offer-page RateOption / SuggestedOption / cheapest rate.
   */
  charterDepartDate?: string | moment.Moment
  isChangePackage?: boolean
  isFlashUSP?: boolean
  hideNonRefundable?: boolean
  /**
   * Optional leading affordance rendered only when a policy branch is visible.
   */
  leadingIcon?: ReactNode
}

/**
 * Redux-aware Flash cancellation entry for call sites that do not already have a policy view
 * (rate options, checkout, etc.). Offer-page USP should render FlashCancellationPoliciesView
 * with the shared surface policyView instead.
 */
function FlashCancellationPolicies(props: Props) {
  const {
    cancellationPolicy,
    checkInDate,
    charterDepartDate,
    propertyTimezoneOffset,
    isChangePackage,
    isFlashUSP,
    hideNonRefundable,
    leadingIcon,
  } = props
  const purchaseDate = useAppSelector(state => state.checkout.cart.existingOrder?.createdAt)
  const isCharterModeEnabled = useAppSelector(isCharterEnabled)
  const charterFlightDepartureDateFromCart = useAppSelector(getCharterFlightDepartureDate)
  // Only fall back when the prop is omitted. Explicit `undefined` means "still resolving".
  const resolvedCharterDepartDate = 'charterDepartDate' in props
    ? charterDepartDate
    : (isCharterCancellationPolicy(cancellationPolicy) ? charterFlightDepartureDateFromCart : undefined)

  const policyView = useMemo(() => getFlashCancellationPolicyView({
    cancellationPolicy,
    propertyTimezoneOffset,
    checkInDate,
    charterDepartDate: resolvedCharterDepartDate,
    purchaseDate,
    isCharterModeEnabled,
    isChangePackage,
    availableForPurchase: true,
  }), [
    cancellationPolicy,
    checkInDate,
    isChangePackage,
    isCharterModeEnabled,
    propertyTimezoneOffset,
    purchaseDate,
    resolvedCharterDepartDate,
  ])

  return (
    <FlashCancellationPoliciesView
      policyView={policyView}
      propertyTimezoneOffset={propertyTimezoneOffset}
      isFlashUSP={isFlashUSP}
      leadingIcon={leadingIcon}
      hideNonRefundable={hideNonRefundable}
    />
  )
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>) {
  const offer = state.offer.offers[ownProps.offerId ?? '']
  const pkg = offer?.packages.find(pkg => pkg.uniqueKey === ownProps.uniqueKey)

  return {
    cancellationPolicy: ownProps.cancellationPolicy || pkg?.roomRate?.cancellationPolicy,
  }
}

export default connect(mapStateToProps)(FlashCancellationPolicies)
